// Any descriptor's list view. Knows nothing about which resource it is showing.
//
// `table-fixed` plus explicit column widths and `truncate`, not `table-layout:
// auto`: an auto table's used width is `max(container, sum of per-column
// min-content)`, so one long macro name pushes the whole card into a horizontal
// scroll. Overflow becomes an ellipsis with the full value in `title`.
//
// NO ZEBRA STRIPING. `--color-sunken` is byte-identical to slate-100 in light mode
// AND is the row hover fill, so a striped row would be invisible under the pointer
// and a third grey would collapse the surface / sunken / hover steps into two.
// Every colour here is a semantic token, so the table flips theme with no `dark:`
// variant — the property the rest of this console has and is worth keeping.

import { Link } from 'react-router';

import { formatCell } from '../../pages/admin/descriptor-form.js';
import { supportPath } from '../../support-path.js';

export default function DescriptorTable({ descriptor, rows }) {
  const columns = descriptor.columns ?? [];

  if (rows.length === 0) {
    return (
      <p className="mt-4 text-sm text-ink-muted">
        No {descriptor.label.toLowerCase()} yet.
      </p>
    );
  }

  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-line bg-surface">
      <table className="min-w-full table-fixed divide-y divide-line text-sm">
        <colgroup>
          {columns.map((column, index) => (
            <col
              key={column.key}
              style={{ width: index === 0 ? '40%' : `${60 / (columns.length - 1 || 1)}%` }}
            />
          ))}
        </colgroup>
        {/* Header, padding and hover copied from the queue table
            (web/src/pages/tickets/List.jsx:291-307) rather than re-invented, so
            the two tables in this app look like the same table. Row hover is the
            surface→sunken step the header also uses: one notch off the row in both
            themes, so the pointer is locatable without a row lighting up. */}
        <thead className="bg-sunken text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col" className="truncate px-4 py-2.5">
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-sunken">
              {columns.map((column, index) => {
                const text = formatCell(row, column.key);
                return (
                  <td key={column.key} className="truncate px-4 py-2.5 text-ink" title={text}>
                    {index === 0 ? (
                      <Link
                        to={supportPath(`/admin/${descriptor.name}/${row.id}`)}
                        className="text-brand-700 hover:underline focus-visible:outline-2
                                   focus-visible:outline-offset-2 focus-visible:outline-brand-600
                                   dark:text-brand-300 dark:focus-visible:outline-brand-400"
                      >
                        {text}
                      </Link>
                    ) : text}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
